import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { sendPasswordlessSignInLink } from '../firebase/authService';
import './SignInOverlay.css';

const SignInOverlay = ({ isOpen, onClose, onSignIn, mode = 'signin', onConfirmSignOut }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  
  // Reset success state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      // Use a small delay to ensure the closing animation completes
      const resetTimeout = setTimeout(() => {
        setShowSuccess(false);
        setEmail('');
        setIsClosing(false);
      }, 50);
      
      return () => clearTimeout(resetTimeout);
    }
  }, [isOpen]);

  // Handle sign out confirmation
  const handleSignOutConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirmSignOut();
      setShowSuccess(true);
    } catch (error) {
      console.error('Sign out error:', error);
      alert('Failed to sign out. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  
  if (!isOpen && !isClosing) return null;

  // Rate limiting: check if user recently sent a sign-in link
  const checkRateLimit = () => {
    const lastSent = localStorage.getItem(`signInLast_${email.trim()}`);
    if (lastSent) {
      const timeDiff = Date.now() - parseInt(lastSent);
      const cooldownTime = 60000; // 1 minute cooldown per email
      if (timeDiff < cooldownTime) {
        const remainingTime = Math.ceil((cooldownTime - timeDiff) / 1000);
        setCooldown(remainingTime);
        
        // Countdown timer
        const timer = setInterval(() => {
          setCooldown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    // Check rate limiting
    if (!checkRateLimit()) {
      return; // Still in cooldown
    }

    setIsLoading(true);
    try {
      await sendPasswordlessSignInLink(email.trim());
      // Store timestamp for rate limiting
      localStorage.setItem(`signInLast_${email.trim()}`, Date.now().toString());
      setShowSuccess(true);
    } catch (error) {
      console.error('Sign in error:', error);
      // You might want to show an error message to the user here
      alert('Failed to send sign-in link. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isClosing) return; // Prevent multiple close calls
    
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 280); // Slightly shorter than animation duration to prevent flash
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className={`signin-overlay ${isClosing ? 'closing' : ''}`}
      onClick={handleBackdropClick}
    >
      <div className={`signin-modal ${isClosing ? 'closing' : ''}`}>
        <div className="signin-header">
          <h2 className="signin-title">
            {mode === 'signout' ? 'Confirm Sign Out' : 'Sign in to continue'}
          </h2>
          <button
            type="button"
            className="signin-close-button"
            onClick={handleClose}
            disabled={isLoading}
          >
            <X size={24} />
          </button>
        </div>

        <div className="signin-content">
          {mode === 'signout' ? (
            // Sign out mode
            showSuccess ? (
              <div className="signin-success">
                <div className="signin-success-icon">
                  ✓
                </div>
                <h3 className="signin-success-title">You're signed out</h3>
                <p className="signin-success-message">
                  You have been successfully signed out. Sign in again anytime to continue using the chat.
                </p>
                <button
                  type="button"
                  className="signin-success-button"
                  onClick={handleClose}
                >
                  Got it
                </button>
              </div>
            ) : (
              <div className="signin-signout-confirm">
                <p className="signin-description">
                  Are you sure you want to sign out? You'll need to sign in again to use the chat.
                </p>
                <div className="signin-signout-buttons">
                  <button
                    type="button"
                    className="signin-cancel-button"
                    onClick={handleClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="signin-signout-button"
                    onClick={handleSignOutConfirm}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="signin-loading-spinner"></div>
                    ) : (
                      'Sign Out'
                    )}
                  </button>
                </div>
              </div>
            )
          ) : (
            // Sign in mode
            showSuccess ? (
              <div className="signin-success">
                <div className="signin-success-icon">
                  ✓
                </div>
                <h3 className="signin-success-title">Check your email!</h3>
                <p className="signin-success-message">
                  We've sent a sign-in link to <strong>{email}</strong>. Click the link in your email to complete the sign-in process.
                </p>
                <button
                  type="button"
                  className="signin-success-button"
                  onClick={handleClose}
                >
                  Got it
                </button>
              </div>
            ) : (
              <>
                <p className="signin-description">
                  Provide your email to unlock the chat. No password required, just sign in and simplify the talent acquisition process
                </p>

                <form onSubmit={handleSubmit} className="signin-form">
                  <div className="signin-input-group">
                    <label htmlFor="signin-email" className="signin-label">
                      Email Address
                    </label>
                    <input
                      id="signin-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email"
                      className="signin-input"
                      required
                      disabled={isLoading}
                    />
                  </div>

                  <button
                    type="submit"
                    className={`signin-submit-button ${isLoading ? 'loading' : ''}`}
                    disabled={!email.trim() || isLoading || cooldown > 0}
                  >
                    {isLoading ? (
                      <div className="signin-loading-spinner"></div>
                    ) : cooldown > 0 ? (
                      `Wait ${cooldown}s before sending again`
                    ) : (
                      'Sign in with Email'
                    )}
                  </button>
                </form>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default SignInOverlay;